// src/screens/Disputes/CreateDisputeScreen.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import './CreateDisputeScreen.css';
import { compressVideo } from '../../utils/compressVideo';
import { compressImage } from '../../utils/compressImage';

import backArrowIcon from '../../assets/backarrow-icon.png';
import addIcon from '../../assets/add-icon.png';
import closeIcon from '../../assets/close-icon.png';

function CreateDisputeScreen({ user }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const [order, setOrder] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [media, setMedia] = useState([]);
  const [timeLeft, setTimeLeft] = useState('');

// screen-only toast (success | error)
const [toast, setToast] = useState({ visible: false, message: '', type: '' });
const showToast = (message, type = 'success', opts = {}) => {
  const { onClose, duration = 2200 } = opts;
  setToast({ visible: true, message, type });
  window.clearTimeout(showToast._t);
  showToast._t = window.setTimeout(() => {
    setToast({ visible: false, message: '', type: '' });
    if (typeof onClose === 'function') onClose();
  }, duration);
};



  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await api.get(`/escrow/${orderId}`);
        setOrder(res.data);
      } catch (err) {
        console.error('❌ Failed to fetch order:', err);
      }
    };
    fetchOrder();
  }, [orderId]);

  useEffect(() => {
    if (!order?.autoReleaseDate) return;

    const updateCountdown = () => {
      const now = new Date();
      const release = new Date(order.autoReleaseDate);
      const diff = release - now;
      if (diff <= 0) return setTimeLeft('Auto-releasing...');
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      setTimeLeft(`${days}d ${hours}h ${minutes}m`);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 60000);
    return () => clearInterval(timer);
  }, [order]);


// ✅ Replace your existing handleMediaChange with this
const handleMediaChange = async (e) => {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;

  let newMedia = [...media];

  for (const file of files) {
    try {
      if (file.type.startsWith('video')) {
        const alreadyHasVideo = newMedia.some(f => f.type.startsWith('video'));
        if (alreadyHasVideo) {
          showToast('Only one video is allowed.', 'error');
          continue;
        }

        // compress video
        try {
          const compressedVideo = await compressVideo(file);
          newMedia.push(compressedVideo);
        } catch (err) {
          console.error('❌ Video compression failed:', err);
          showToast('Failed to compress video.', 'error');
        }
      } else if (file.type.startsWith('image')) {
        const currentImageCount = newMedia.filter(f => f.type.startsWith('image')).length;
        if (currentImageCount >= 5) {
          showToast('Maximum 5 images allowed.', 'error');
          continue;
        }

        // compress image
        try {
          const compressedImage = await compressImage(file);
          newMedia.push(compressedImage);
        } catch (err) {
          console.error('❌ Image compression failed:', err);
          showToast('Failed to compress image.', 'error');
        }
      } else {
        showToast('Unsupported file type.', 'error');
      }
    } catch (err) {
      console.error('❌ Media handling error:', err);
      showToast('Failed to add one of the files.', 'error');
    }
  }

  setMedia(newMedia);
  // reset input so picking the same file again still triggers onChange
  if (fileInputRef.current) fileInputRef.current.value = '';
};


  const handleRemoveFile = (index) => {
    setMedia((prev) => prev.filter((_, i) => i !== index));
  };


// ✅ Replace your existing handleSubmit with this
const handleSubmit = async () => {
  if (!disputeReason.trim()) {
    showToast('Please describe the dispute reason.', 'error');
    return;
  }

  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('orderId', order._id);  // escrow ID
  formData.append('reason', disputeReason.trim());
  media.forEach((file) => formData.append('media', file));

  try {
    const res = await api.post('/disputes', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });

    console.log('✅ Dispute submitted:', res.data);
    showToast(' Dispute submitted successfully!', 'success', {
      onClose: () => navigate('/orders'),
    });
  } catch (err) {
    console.error('❌ Dispute submission failed:', err);
    if (err.response) {
      console.log('🔴 Backend response:', err.response.data);
      console.log('🔴 Status:', err.response.status);
    }
    const msg = err?.response?.data?.message || '❌ Dispute submission failed. Please try again.';
    showToast(msg, 'error');
  }
};



  if (!order) return <p>Loading...</p>;

  return (
    <div className="disputeform-container">
      {/* Fixed Top Bar */}
      <div className="disputeform-header">
        <img src={backArrowIcon} alt="Back" onClick={() => navigate(-1)} />
        <h2>Dispute Order</h2>
      </div>

      {/* Scrollable Body */}
      <div className="disputeform-body">
        <p><strong>Product:</strong> {order.productId.name}</p>
        <p><strong>Seller:</strong> @{order.seller}</p>
        <p><strong>Buyer:</strong> @{order.buyer}</p>
        <p><strong>Order Date:</strong> {new Date(order.createdAt).toLocaleString()}</p>
        <p><strong>Time Remaining:</strong> {timeLeft}</p>

        <label>Dispute Reason</label>
        <textarea
          value={disputeReason}
          onChange={(e) => setDisputeReason(e.target.value)}
          rows={4}
          placeholder="Explain your dispute clearly..."
        />

        <label>Supporting Documents (max 5)</label>
        <div className="disputeform-media-preview">
          {media.map((file, i) => (
            <div key={i} className="preview-thumb">
              {file.type.startsWith('video') ? (
                <video src={URL.createObjectURL(file)} controls />
              ) : (
                <img src={URL.createObjectURL(file)} alt="preview" />
              )}

<div className="dispute-remove-icon-wrapper" onClick={() => handleRemoveFile(i)}>
  <img src={closeIcon} className="dispute-remove-icon" alt="remove" />
</div>



            </div>
          ))}
          {media.length < 5 && (
            <div className="add-media-box">
              <label htmlFor="dispute-media">
                <img src={addIcon} alt="Add" />
              </label>
              <input
                id="dispute-media"
                type="file"
                accept="image/*,video/*"
                multiple
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleMediaChange}
              />
            </div>
          )}
        </div>
      </div>

      <div className="disputeform-footer">
        <button className="submit-dispute-btn" onClick={handleSubmit}>
          Submit Dispute
        </button>
      </div>

{toast.visible && (
  <div className={`dispute-toast ${toast.type}`}>
    {toast.message}
  </div>
)}

    </div>
  );
}

export default CreateDisputeScreen;
